import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Translation } from '../commonTypes/translation.types';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type StringWithoutDot<T extends string> = T extends `${infer _U}.${infer _V}` ? undefined : T;

function replacePlaceholders(template: string, replacements: Record<string, unknown>): string {
    const regex = /{{(.*?)}}/g;
    return template.replace(regex, (_, key) => {
        return key in replacements ? `${replacements[key] as string}` : `{{${key}}}`;
    });
}

export function useTranslation<T extends StringWithoutDot<keyof Translation>>(translationPrefix?: T) {
    const data = useSelector<RootState, Translation | Translation[keyof Translation] | undefined>((state: RootState) => {
        const data = state.language.data;
        if (!translationPrefix) return data;
        return data?.[translationPrefix];
    });

    function t<K extends T extends keyof Translation ? keyof Translation[T] : keyof Translation>(
        translation: undefined extends T ? keyof Translation : K,
        replacements?: Record<string, string>
    ): string {
        try {
            if (!data) throw new Error('Missing data from redux store');

            const keys = (translation as string).split('.');
            let result = data as any;

            for (const element of keys) {
                result = result[element];

                if (!result) break;
            }

            if (typeof result !== 'string') throw new Error('Result is not a string');
            if (replacements) return replacePlaceholders(result, replacements);
            return result;
        } catch (e) {
            console.log('Error in translation for key', translation, e);
            return translation ?? '...';
        }
    }

    return { t, data: data as Translation };
}
